import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  findIdempotentWritePath,
  IDEMPOTENCY_KEY_HEADER,
  isIdempotencyConflictError,
} from '../helpers/idempotency.helper';
import { IdempotencyKeyService } from '../services/idempotency-key.service';

/**
 * Attaches the mandatory `X-Idempotency-Key` to every POST that targets a registered idempotent write
 * endpoint, and manages that key's lifetime.
 *
 * Doing it here rather than at each call site is what makes the guarantee hold: a new write endpoint
 * only has to be listed in `IDEMPOTENT_WRITE_PATH` and it is covered — no API-service argument to
 * remember to thread through, no component that can forget. It also makes automatic retries correct
 * for free: an rxjs `retry()` re-runs this interceptor with the same body, and the key is derived
 * from that body, so the retry reuses the key instead of looking like a second submission.
 */
@Injectable()
export class IdempotencyInterceptor implements HttpInterceptor {
  constructor(private idempotencyKeyService: IdempotencyKeyService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const scope = req.method === 'POST' ? findIdempotentWritePath(req.url) : undefined;

    if (!scope) {
      return next.handle(req);
    }

    // Params count towards identity, not just the body: /payment/successHandler posts a null body and
    // carries its only discriminator, sessionId, in the query string. Fingerprinting on the body
    // alone would make two different donations look like one replayed submission.
    // An explicit header wins, so a caller with a better notion of "one submission" can override.
    const key =
      req.headers.get(IDEMPOTENCY_KEY_HEADER) ||
      this.idempotencyKeyService.keyFor(scope, { body: req.body, params: req.params.toString() });

    return next.handle(req.clone({ headers: req.headers.set(IDEMPOTENCY_KEY_HEADER, key) })).pipe(
      tap((event) => {
        // The submission landed. The key is spent, so a later identical one is treated as new
        // rather than deduplicated away.
        if (event instanceof HttpResponse) {
          this.idempotencyKeyService.release(scope);
        }
      }),
      catchError((error) => {
        // A conflicting replay can never succeed with this key — drop it so the user's next attempt
        // is a clean submission. Every other failure keeps the key, so retrying cannot double-book.
        if (isIdempotencyConflictError(error)) {
          this.idempotencyKeyService.release(scope);
        }
        return throwError(error);
      })
    );
  }
}
